import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { array, number, object, ObjectSchema, string } from 'yup'
import { CandidateFullName } from '../chart-general-form'
import {
  AdminChartBaseFormValues,
  adminChartBaseValidationSchema,
} from './admin-base'

export type AdminNonPartisanChartRaceFormValues = {
  id?: string
  name: string
  type: string
  raceType: string
  candidates: number
  candidateNames: CandidateFullName[]
  writeIns: number
  voteFor: number
}

export type AdminNonPartisanChartFormValues = AdminChartBaseFormValues & {
  races: AdminNonPartisanChartRaceFormValues[]
}

const adminChartValidationSchema: ObjectSchema<AdminNonPartisanChartFormValues> =
  object()
    .shape({
      ...adminChartBaseValidationSchema,
      races: array()
        .of(
          object().shape(
            {
              id: string(),
              name: string().required(),
              type: string().required(),
              raceType: string().required(),
              writeIns: number()
                .min(0)
                .default(1)
                .required()
                .when(['type', 'candidates'], ([type, candidates], schema) => {
                  return type === 'Race' && candidates === 0
                    ? schema.min(
                        1,
                        'Race must have either candidates or write-ins'
                      )
                    : schema
                }),
              voteFor: number()
                .required()
                .when('type', {
                  is: 'Race',
                  then: (schema) => schema.min(1).max(100).required(),
                  otherwise: (schema) => schema.optional(),
                }),
              candidates: number()
                .min(0)
                .max(100)
                .default(0)
                .required('candidates is required')
                .when(['type', 'writeIns'], ([type, writeIns], schema) => {
                  return type === 'Race' && writeIns === 0
                    ? schema.min(
                        1,
                        'Race must have either candidates or write-ins'
                      )
                    : schema
                }),
              candidateNames: array()
                .of(
                  object({
                    name: string().required(),
                    order: number().required(),
                  })
                )
                .required()
                .when(['showCandidateNames', 'type'], {
                  is: (show: boolean, type: string) =>
                    show === true && type === 'Race',
                  then: (schema) => schema.min(1).required(),
                  otherwise: (schema) => schema.optional(),
                }),
            },
            [
              ['candidates', 'writeIns'],
              ['type', 'candidates'],
              ['writeIns', 'type'],
            ]
          )
        )
        .required()
        .test({
          message: 'Chart must have at least one race',
          test: (arr) => {
            if (!arr) {
              return false
            }
            return arr!.length > 0
          },
        }),
    })
    .required()

export function useAdminNonPartisanChartForm() {
  return useForm<AdminNonPartisanChartFormValues>({
    resolver: yupResolver(adminChartValidationSchema),
    /* comment out above resolver if form isn't submitting and uncomment this block to debug
    resolver: async (data, context, options) => {
      // you can debug your validation schema here
      console.log("formData", data)
      console.log(
        "validation result",
        await yupResolver(adminChartValidationSchema)(data, context, options)
      )
      return yupResolver(adminChartValidationSchema)(data, context, options)
    },*/
    reValidateMode: 'onChange',
    mode: 'onChange',
  })
}
